import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Eye, Printer, Share2, Download } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const ReceiptViewer = ({ payment, isOpen, onClose }) => {
  if (!payment) return null;

  const logoUrl = 'https://customer-assets.emergentagent.com/job_edumanage-196/artifacts/5gc25pr5_GeniusKid%20Logo.svg';

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Receipt border
    doc.setLineWidth(0.5);
    doc.rect(10, 10, 190, 277);
    
    // Header
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('PAYMENT RECEIPT', 105, 25, { align: 'center' });
    
    // Company Info with Logo placeholder
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('GeniusKid Education', 105, 45, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('www.geniuskid.org', 105, 51, { align: 'center' });
    
    // Receipt Details Box
    doc.setFillColor(240, 240, 240);
    doc.rect(20, 65, 170, 40, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.rect(20, 65, 170, 40);
    
    // Receipt Number
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Receipt No:', 25, 75);
    doc.setFont('helvetica', 'normal');
    doc.text(`#${payment.id.substring(0, 8).toUpperCase()}`, 55, 75);
    
    // Date
    doc.setFont('helvetica', 'bold');
    doc.text('Date:', 120, 75);
    doc.setFont('helvetica', 'normal');
    doc.text(new Date(payment.payment_date).toLocaleDateString(), 135, 75);
    
    // Invoice Reference
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice #:', 25, 85);
    doc.setFont('helvetica', 'normal');
    doc.text(`${payment.invoice_number || 'N/A'}`, 55, 85);
    
    // Payment Method
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Method:', 25, 95);
    doc.setFont('helvetica', 'normal');
    doc.text(payment.payment_method.toUpperCase(), 70, 95);
    
    // Transaction/Cheque ID
    if (payment.transaction_id || payment.cheque_number) {
      doc.setFont('helvetica', 'bold');
      doc.text(payment.payment_method === 'cheque' ? 'Cheque No:' : 'Transaction ID:', 120, 95);
      doc.setFont('helvetica', 'normal');
      const refNumber = payment.cheque_number || payment.transaction_id || '';
      doc.text(refNumber.substring(0, 20), 120, 100);
    }
    
    // Received From Section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Received From:', 20, 125);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text(payment.student_name || 'Student', 20, 133);
    
    // Amount Box
    doc.setFillColor(59, 130, 246);
    doc.rect(20, 150, 170, 30, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('AMOUNT RECEIVED:', 25, 162);
    doc.setFontSize(18);
    doc.text(`₹${payment.amount.toFixed(2)}`, 25, 173);
    doc.setTextColor(0, 0, 0);
    
    // Amount in Words
    const amountInWords = numberToWords(payment.amount);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.text(`(${amountInWords} Rupees Only)`, 20, 190);
    
    // Notes
    if (payment.notes) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Notes:', 20, 205);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const splitNotes = doc.splitTextToSize(payment.notes, 170);
      doc.text(splitNotes, 20, 213);
    }
    
    // Signature Section
    doc.setDrawColor(0, 0, 0);
    doc.line(130, 255, 180, 255);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Authorized Signature', 140, 261);
    
    // Footer
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text('This is a computer generated receipt and does not require a signature.', 105, 275, { align: 'center' });
    doc.text('Thank you for your payment!', 105, 280, { align: 'center' });
    
    return doc;
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    if (num === 0) return 'Zero';
    
    const convert = (n) => {
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' and ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 !== 0 ? ' ' + convert(n % 1000) : '');
      return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 !== 0 ? ' ' + convert(n % 100000) : '');
    };
    
    return convert(Math.floor(num));
  };

  const handleViewPDF = () => {
    const doc = generatePDF();
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  };

  const handleDownloadPDF = () => {
    const doc = generatePDF();
    doc.save(`Receipt-${payment.id.substring(0, 8)}.pdf`);
    toast.success('Receipt downloaded successfully!');
  };

  const handlePrint = () => {
    const doc = generatePDF();
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdfUrl, '_blank');
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const handleShare = async () => {
    const doc = generatePDF();
    const pdfBlob = doc.output('blob');
    const file = new File([pdfBlob], `Receipt-${payment.id.substring(0, 8)}.pdf`, { type: 'application/pdf' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: `Receipt #${payment.id.substring(0, 8)}`,
          text: `Payment receipt for ${payment.student_name}`,
          files: [file]
        });
        toast.success('Receipt shared successfully!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          fallbackShare();
        }
      }
    } else {
      fallbackShare();
    }
  };

  const fallbackShare = () => {
    const doc = generatePDF();
    doc.save(`Receipt-${payment.id.substring(0, 8)}.pdf`);
    toast.success('Receipt downloaded! You can now share it manually.');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Payment Receipt</DialogTitle>
        </DialogHeader>

        {/* Receipt Preview */}
        <div className="border-2 rounded-lg p-6 bg-gray-50">
          <div className="bg-white p-8 rounded shadow-sm border-2 border-gray-300">
            {/* Header */}
            <div className="border-b-2 pb-6 mb-6">
              <div className="flex items-center justify-center gap-4 mb-4">
                <img src={logoUrl} alt="GeniusKid Logo" className="h-12" />
              </div>
              <h1 className="text-3xl font-bold text-center text-blue-600">PAYMENT RECEIPT</h1>
              <p className="text-center text-sm text-gray-600 mt-2">GeniusKid Education</p>
              <p className="text-center text-xs text-gray-500">www.geniuskid.org</p>
            </div>

            {/* Receipt Details */}
            <div className="bg-gray-100 p-4 rounded mb-6 grid grid-cols-2 gap-4">
              <div>
                <span className="font-semibold">Receipt No:</span> #{payment.id.substring(0, 8).toUpperCase()}
              </div>
              <div>
                <span className="font-semibold">Date:</span> {new Date(payment.payment_date).toLocaleDateString()}
              </div>
              <div>
                <span className="font-semibold">Invoice #:</span> {payment.invoice_number || 'N/A'}
              </div>
              <div>
                <span className="font-semibold">Payment Method:</span> {payment.payment_method.toUpperCase()}
              </div>
              {(payment.transaction_id || payment.cheque_number) && (
                <div className="col-span-2">
                  <span className="font-semibold">
                    {payment.payment_method === 'cheque' ? 'Cheque No:' : 'Transaction ID:'}
                  </span> {payment.cheque_number || payment.transaction_id}
                </div>
              )}
            </div>

            {/* Received From */}
            <div className="mb-6">
              <h3 className="font-bold text-lg mb-2">Received From:</h3>
              <p className="text-xl">{payment.student_name || 'Student'}</p>
            </div>

            {/* Amount */}
            <div className="bg-blue-500 text-white p-6 rounded mb-6">
              <div className="text-sm mb-2">AMOUNT RECEIVED:</div>
              <div className="text-4xl font-bold">₹{payment.amount.toFixed(2)}</div>
              <div className="text-sm mt-2 opacity-90 italic">
                ({numberToWords(payment.amount)} Rupees Only)
              </div>
            </div>

            {/* Notes */}
            {payment.notes && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2">Notes:</h3>
                <p className="text-sm text-gray-600 italic">{payment.notes}</p>
              </div>
            )}

            {/* Signature */}
            <div className="flex justify-end mt-12">
              <div className="text-center">
                <div className="border-t-2 border-black w-48 mb-2"></div>
                <p className="text-sm text-gray-600">Authorized Signature</p>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center text-xs text-gray-500 italic border-t mt-6 pt-4">
              <p>This is a computer generated receipt and does not require a signature.</p>
              <p className="mt-1">Thank you for your payment!</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button onClick={handleViewPDF} variant="outline" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            View PDF
          </Button>
          <Button onClick={handleDownloadPDF} variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Download
          </Button>
          <Button onClick={handlePrint} variant="outline" className="flex items-center gap-2">
            <Printer className="w-4 h-4" />
            Print
          </Button>
          <Button onClick={handleShare} className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500">
            <Share2 className="w-4 h-4" />
            Share
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReceiptViewer;
